import { TranslateFunc } from '../types';

interface SubDomainInfo {
  localdev?: string;
  qa?: string;
  default: string;
}

export interface KitLocaleInfo {
  localeCode?: string;
  languageCode: string;
  localizedLanguageName: string;
  corporateUrl: string;
  nosSubdomain: SubDomainInfo;
  nosCookiePrefLanguage: string;
  nosCookieLocale: string;
}

export interface KitRegionInfo {
  regionCode: 'NA-US' | 'NA-CA' | 'EU' | 'ANZ';
  regionName: string;
  ssoSubdomain: string;
  defaultLocaleCode: string;
  displayCaliforniaPrivacy?: boolean;
  californiaPrivacyPath?: string;
  locales: KitLocaleInfo[];
}

export interface KitLocaleConfig {
  privacyPolicyPath: string;
  signupPath: string;
  regions: KitRegionInfo[];
}

export const DEFAULT_LOCALE = 'en-US';
export const KIT_LOCALE_DEFAULT_REGION_CODE = 'NA-US';
export const KIT_LOCALE_DEFAULT_LOCALE_CODE = DEFAULT_LOCALE;

export const SUPPORTED_LOCALES = new Map([
  ['en-AU', 'en-GB'],
  ['en-GB', 'en-GB'],
  ['en-NZ', 'en-GB'],
  ['fr-CA', 'fr-CA'],
  ['pt-PT', 'pt-PT'],
  ['pl-PL', 'pl-PL'],
  ['cs-CZ', 'cs-CZ'],
  ['es-LA', 'es-LA'],
  ['es-MX', 'es-LA'],
  ['es-419', 'es-LA'],

  ['en', 'en-US'],
  ['fr', 'fr-FR'],
  ['es', 'es-ES'],
  ['de', 'de-DE'],
  ['it', 'it-IT'],
  ['nl', 'nl-NL'],
  ['sv', 'sv-SE'],
  ['nb', 'nb-NO'],
  ['da', 'da-DK'],
]);

// this can be set via setLocale which will change the default locale that is used by the various number formatters
let locale = DEFAULT_LOCALE;

export enum Currency {
  AU = 'AUD',
  CA = 'CAD',
  DE = 'EUR',
  ES = 'EUR',
  FR = 'EUR',
  IT = 'EUR',
  GB = 'GBP',
  MX = 'MXD',
  NL = 'EUR',
  PT = 'EUR',
  NO = 'kr',
  NZ = 'NZD',
  US = 'USD',
}

export function getLocale() {
  return locale;
}

// this is should usually be set once on page load
export function setLocale(loc: string) {
  const result = getSupportedLocale(loc);
  locale = result;
}

export function getSupportedLocale(loc: string): string {
  let locale = SUPPORTED_LOCALES.get(loc);
  if (!locale) {
    // iOS can set only 'de' in webview
    const mainLocale = loc?.substring(0, 2).toLowerCase();
    locale = SUPPORTED_LOCALES.get(mainLocale);
  }

  return locale || DEFAULT_LOCALE;
}

export const parseLocale = (loc: string): Record<string, string> => {
  const parts = getSupportedLocale(loc).split('-');
  const [languageCode, countryCode] = parts;

  return {
    languageCode,
    countryCode,
    locale: `${languageCode}-${countryCode}`,
  };
};

/**
 * @returns Returns browser locale
 * Note: This first tries to get the locale from the browser
 * It uses getSupportedLocale to ensure the browser locale is one we recognize at ChargePoint
 */
export const getBrowserLocale = (): string => {
  // Note: navigator.language appears to be returning the correct locale for Chrome, Firefox, & Safari
  // TODO: test results for different devices: i0S, Android, etc
  const { language } = window.navigator as Navigator;
  return getSupportedLocale(language);
};

export function getCountryCodeFromLocale(loc: string): string {
  const supportedLocale = getSupportedLocale(loc ?? locale);
  // eslint-disable-next-line no-unused-vars
  return supportedLocale.split('-')[1];
}

function getKitLocaleConfig(t: TranslateFunc): KitLocaleConfig {
  return {
    privacyPolicyPath: '/privacy_policy',
    signupPath: '/signup',
    regions: [
      {
        regionCode: 'NA-US',
        regionName: t('kit:locale_picker.region_names.NA-US'),
        ssoSubdomain: 'sso',
        defaultLocaleCode: 'en-US',
        displayCaliforniaPrivacy: true,
        californiaPrivacyPath: '/privacy_policy#CCPA',
        locales: [
          {
            localeCode: 'en-US',
            languageCode: 'en',
            localizedLanguageName: 'English',
            corporateUrl: 'www.chargepoint.com',
            nosSubdomain: {
              localdev: 'cpnos',
              qa: 'cpnos',
              default: 'na',
            },
            nosCookiePrefLanguage: 'en',
            nosCookieLocale: 'en-US',
          },
          {
            localeCode: 'es-LA',
            languageCode: 'es',
            localizedLanguageName: 'Español',
            corporateUrl: 'www.chargepoint.com/es-mx',
            nosSubdomain: {
              default: 'mx',
            },
            nosCookiePrefLanguage: 'es_la',
            nosCookieLocale: 'es-LA',
          },
          {
            localeCode: 'fr-CA',
            languageCode: 'fr',
            localizedLanguageName: 'Français',
            corporateUrl: 'www.chargepoint.com/fr-ca',
            nosSubdomain: {
              localdev: 'cpnos',
              qa: 'cpnos',
              default: 'na',
            },
            nosCookiePrefLanguage: 'fr_ca',
            nosCookieLocale: 'fr-CA',
          },
        ],
      },
      {
        regionCode: 'NA-US',
        regionName: t('kit:locale_picker.region_names.NA-MX'),
        ssoSubdomain: 'sso',
        defaultLocaleCode: 'es-LA',
        locales: [
          {
            localeCode: 'es-LA',
            languageCode: 'es',
            localizedLanguageName: 'Español',
            corporateUrl: 'www.chargepoint.com/es-mx',
            nosSubdomain: {
              default: 'mx',
            },
            nosCookiePrefLanguage: 'es_la',
            nosCookieLocale: 'es-LA',
          },
        ],
      },
      {
        regionCode: 'NA-CA',
        regionName: t('kit:locale_picker.region_names.NA-CA'),
        ssoSubdomain: 'sso-ca',
        defaultLocaleCode: 'en-CA',
        locales: [
          {
            localeCode: 'en-CA',
            languageCode: 'en',
            localizedLanguageName: 'English',
            corporateUrl: 'www.chargepoint.com/en-ca',
            nosSubdomain: {
              default: 'ca',
            },
            nosCookiePrefLanguage: 'en_ca',
            nosCookieLocale: 'en-CA',
          },
          {
            localeCode: 'fr-CA',
            languageCode: 'fr',
            localizedLanguageName: 'Français',
            corporateUrl: 'www.chargepoint.com/fr-ca',
            nosSubdomain: {
              default: 'ca',
            },
            nosCookiePrefLanguage: 'fr_ca',
            nosCookieLocale: 'fr-CA',
          },
        ],
      },
      {
        regionCode: 'EU',
        regionName: t('kit:locale_picker.region_names.EU'),
        ssoSubdomain: 'sso-eu',
        defaultLocaleCode: 'en-GB',
        locales: [
          {
            localeCode: 'cs-CZ',
            languageCode: 'cs',
            localizedLanguageName: 'čeština',
            corporateUrl: 'www.chargepoint.com',
            nosSubdomain: {
              default: 'cs',
            },
            nosCookiePrefLanguage: 'cs_cz',
            nosCookieLocale: 'cs-CZ',
          },
          {
            localeCode: 'da-DK',
            languageCode: 'da',
            localizedLanguageName: 'Dansk',
            corporateUrl: 'www.chargepoint.com/da-dk',
            nosSubdomain: {
              default: 'da',
            },
            nosCookiePrefLanguage: 'da_dk',
            nosCookieLocale: 'da-DK',
          },
          {
            localeCode: 'de-DE',
            languageCode: 'de',
            localizedLanguageName: 'Deutsch',
            corporateUrl: 'www.chargepoint.com/de-de',
            nosSubdomain: {
              default: 'de',
            },
            nosCookiePrefLanguage: 'de_de',
            nosCookieLocale: 'de-DE',
          },
          {
            localeCode: 'en-GB',
            languageCode: 'en',
            localizedLanguageName: 'English',
            corporateUrl: 'www.chargepoint.com/en-gb',
            nosSubdomain: {
              default: 'uk',
            },
            nosCookiePrefLanguage: 'en_gb',
            nosCookieLocale: 'en-GB',
          },
          {
            localeCode: 'es-ES',
            languageCode: 'es',
            localizedLanguageName: 'Español',
            corporateUrl: 'www.chargepoint.com/es-es',
            nosSubdomain: {
              default: 'es',
            },
            nosCookiePrefLanguage: 'es_es',
            nosCookieLocale: 'es-ES',
          },
          {
            localeCode: 'fr-FR',
            languageCode: 'fr',
            localizedLanguageName: 'Français',
            corporateUrl: 'www.chargepoint.com/fr-fr',
            nosSubdomain: {
              default: 'fr',
            },
            nosCookiePrefLanguage: 'fr_fr',
            nosCookieLocale: 'fr-FR',
          },
          {
            localeCode: 'it-IT',
            languageCode: 'it',
            localizedLanguageName: 'Italiano',
            corporateUrl: 'www.chargepoint.com/it-it',
            nosSubdomain: {
              default: 'it',
            },
            nosCookiePrefLanguage: 'it_it',
            nosCookieLocale: 'it-IT',
          },

          {
            localeCode: 'nl-NL',
            languageCode: 'nl',
            localizedLanguageName: 'Nederlands',
            corporateUrl: 'www.chargepoint.com/nl-nl',
            nosSubdomain: {
              default: 'nl',
            },
            nosCookiePrefLanguage: 'nl_nl',
            nosCookieLocale: 'nl-NL',
          },
          {
            localeCode: 'nb-NO',
            languageCode: 'nb',
            localizedLanguageName: 'Norsk Bokmål',
            corporateUrl: 'www.chargepoint.com/no-no',
            nosSubdomain: {
              default: 'no',
            },
            nosCookiePrefLanguage: 'nb_no',
            nosCookieLocale: 'nb-NO',
          },
          {
            localeCode: 'pl-PL',
            languageCode: 'pl',
            localizedLanguageName: 'Polskie',
            corporateUrl: 'www.chargepoint.com',
            nosSubdomain: {
              default: 'pl',
            },
            nosCookiePrefLanguage: 'pl_pl',
            nosCookieLocale: 'pl-PL',
          },
          {
            localeCode: 'pt-PT',
            languageCode: 'pt',
            localizedLanguageName: 'Português',
            corporateUrl: 'www.chargepoint.com/pt-pt',
            nosSubdomain: {
              default: 'pt',
            },
            nosCookiePrefLanguage: 'pt_pt',
            nosCookieLocale: 'pt-PT',
          },
          {
            localeCode: 'sv-SE',
            languageCode: 'sv',
            localizedLanguageName: 'Svenska',
            corporateUrl: 'www.chargepoint.com/sv-se',
            nosSubdomain: {
              default: 'sv',
            },
            nosCookiePrefLanguage: 'sv_se',
            nosCookieLocale: 'sv-SE',
          },
        ],
      },
      {
        regionCode: 'ANZ',
        regionName: t('kit:locale_picker.region_names.ANZ'),
        ssoSubdomain: 'sso-au',
        defaultLocaleCode: 'en-AU',
        locales: [
          {
            localeCode: 'en-AU',
            languageCode: 'en',
            localizedLanguageName: 'English',
            corporateUrl: 'www.chargepoint.com',
            nosSubdomain: {
              default: 'au',
            },
            nosCookiePrefLanguage: 'en_au',
            nosCookieLocale: 'en-AU',
          },
        ],
      },
    ],
  };
}

export function getRegionCodeForSSOSubdomain(t: TranslateFunc, ssoSubdomain: string): string | undefined {
  const localeConfig = getKitLocaleConfig(t);

  const region = localeConfig.regions.find((r) => {
    return r.ssoSubdomain === ssoSubdomain;
  });

  return region?.regionCode;
}

export function getDefaultLocaleCodeForRegionCode(t: TranslateFunc, regionCode: string): string | undefined {
  const localeConfig = getKitLocaleConfig(t);

  const region = localeConfig.regions.find((r) => {
    return r.regionCode === regionCode;
  });

  return region?.defaultLocaleCode;
}

/**
 * Only to be used as a fallback for when a proper locale cannot be derived
 * @param lang -- lang code en, fr, de, ...etc
 */
export function getDefaultCountryCodeFromLanguageCode(lang: string): string {
  const loc = getSupportedLocale(lang);
  return loc.split('-')[1];
}

export function getCurrencyCode(countryCode: string) {
  return Currency[countryCode as keyof Currency];
}

const KitLocale = {
  getSupportedLocale,
  getKitLocaleConfig,
  getRegionCodeForSSOSubdomain,
  getDefaultLocaleCodeForRegionCode,
};

export default KitLocale;
